// Menu "Outros assuntos": temas avulsos (fora de projetos), cada um com seu fio
// de discussão e histórico. Busca por palavra-chave, cria novos, e (admin)
// encerra/reabre/exclui.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/rbac';
import { loadPeople, type Person } from '../lib/notifications';
import { createDiscussion, loadAssuntos, type Discussion } from '../lib/encaminhamentos';
import { DiscussionThread } from '../components/DiscussionThread';

export function OutrosAssuntos() {
  const { currentOrgId, userId, fullName, grants } = useAuth();
  const admin = grants ? isAdmin(grants) : false;
  const canPost = grants ? grants.role !== 'viewer' : false;

  const [assuntos, setAssuntos] = useState<Discussion[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showClosed, setShowClosed] = useState(true);
  const [novo, setNovo] = useState(false);

  const reload = () => {
    if (!currentOrgId) return;
    loadAssuntos(currentOrgId, { includeClosed: true }).then(setAssuntos);
  };
  useEffect(() => {
    if (!currentOrgId) return;
    setLoading(true);
    Promise.all([loadAssuntos(currentOrgId, { includeClosed: true }), loadPeople(currentOrgId)])
      .then(([a, p]) => {
        setAssuntos(a);
        setPeople(p);
      })
      .finally(() => setLoading(false));
  }, [currentOrgId]);

  const filtrados = useMemo(() => {
    const term = q.trim().toLowerCase();
    return assuntos.filter((a) => {
      if (!showClosed && a.status === 'encerrado') return false;
      if (!term) return true;
      const inTitle = a.title.toLowerCase().includes(term);
      const inMsgs = a.messages.some((m) => (m.body ?? '').toLowerCase().includes(term));
      return inTitle || inMsgs;
    });
  }, [assuntos, q, showClosed]);

  const abertos = assuntos.filter((a) => a.status === 'aberto').length;

  return (
    <>
      <div className="topbar">
        <h1>Outros assuntos</h1>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n tabular">{abertos}</span>
            <span className="l">Abertos</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{assuntos.length}</span>
            <span className="l">Total</span>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="card card-pad">
          <div className="filters" style={{ alignItems: 'center' }}>
            <input
              type="search"
              placeholder="Buscar por palavra-chave…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <label className="field-inline" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              <span className="muted">mostrar encerrados</span>
            </label>
            {canPost && (
              <button className="btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setNovo((v) => !v)}>
                {novo ? 'Cancelar' : '＋ Novo assunto'}
              </button>
            )}
          </div>
          {novo && (
            <NovoAssunto
              orgId={currentOrgId!}
              userId={userId}
              fullName={fullName}
              people={people}
              onCreated={() => {
                setNovo(false);
                reload();
              }}
            />
          )}
        </div>

        {loading ? (
          <div className="center-note">Carregando…</div>
        ) : filtrados.length === 0 ? (
          <div className="card card-pad">
            <p className="muted">
              {assuntos.length === 0
                ? 'Nenhum assunto ainda. Crie um aqui ou pela reunião semanal.'
                : 'Nada encontrado para essa busca.'}
            </p>
          </div>
        ) : (
          filtrados.map((a) => (
            <div className="card card-pad" key={a.id} style={{ marginBottom: 12 }}>
              <DiscussionThread
                d={a}
                userId={userId}
                fullName={fullName}
                isAdmin={admin}
                onChanged={reload}
                showProjectLink={false}
              />
            </div>
          ))
        )}
      </div>
    </>
  );
}

function NovoAssunto({
  orgId,
  userId,
  fullName,
  people,
  onCreated,
}: {
  orgId: string;
  userId: string | null;
  fullName: string | null;
  people: Person[];
  onCreated: () => void;
}) {
  const [nome, setNome] = useState('');
  const [coment, setComent] = useState('');
  const [pid, setPid] = useState('');
  const [busy, setBusy] = useState(false);

  async function criar() {
    if (!nome.trim()) return;
    setBusy(true);
    const person = people.find((p) => p.id === pid);
    const { error } = await createDiscussion({
      orgId,
      kind: 'assunto',
      title: nome.trim(),
      productId: null,
      assignedToId: pid || null,
      assignedName: person?.name ?? null,
      createdById: userId,
      createdByName: fullName,
      firstMessage: coment.trim() || nome.trim(),
    });
    setBusy(false);
    if (error) return alert(error);
    onCreated();
  }

  return (
    <div className="novo-assunto" style={{ marginTop: 10 }}>
      <div className="reuniao-outros">
        <input placeholder="Nome do assunto (ex.: MÁQUINA DE ENVASE)…" value={nome} onChange={(e) => setNome(e.target.value)} />
        <select value={pid} onChange={(e) => setPid(e.target.value)}>
          <option value="">Responsável (opcional)…</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="note-textarea"
        rows={2}
        placeholder="Primeiro comentário…"
        value={coment}
        onChange={(e) => setComent(e.target.value)}
        style={{ marginTop: 6 }}
      />
      <div className="reuniao-actions">
        <button className="btn-primary btn-sm" disabled={busy || !nome.trim()} onClick={criar}>
          {busy ? 'Criando…' : 'Criar assunto'}
        </button>
      </div>
    </div>
  );
}
